import { WindowWithRetailRocket } from '@analytics/const';
import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { environment } from '@core/const';
import { CookieService } from '@core/feature';
import { CustomOrder } from '@order/const';
import { OrderEntry, Product, WindowRef } from '@spartacus/core';
import { User } from '@user/const';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { DebugAnalyticsService } from './debug-analytics.service';

@Injectable({ providedIn: 'root' })
export class RetailRocketService implements OnDestroy {
  private subscription = new Subscription();
  private sessionId$ = new ReplaySubject<string | null>(1);

  private get window(): WindowWithRetailRocket {
    return this.windowRef.nativeWindow as WindowWithRetailRocket;
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private windowRef: WindowRef,
    private debugAnalyticsService: DebugAnalyticsService,
    private cookieService: CookieService,
  ) {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  initInBrowser(): void {
    if (environment.production) {
      this.sendEvent(() => this.extractSessionFromCookie());
    } else if (this.debugAnalyticsService.debug) {
      this.extractSessionFromCookie();
      this.initDebug();
    } else {
      this.extractSessionFromCookie();
    }
  }

  initInServer(): void {
    this.extractSessionFromCookie();
  }

  onViewProductPage({ product, activeBaseStoreCode }: { product: Product; activeBaseStoreCode: string }): void {
    this.sendEvent(() => {
      this.window.rrApi.view(+product.code, { stockId: activeBaseStoreCode });
    });
  }

  onViewCategoryPage({ categoryId }: { categoryId: number }): void {
    this.sendEvent(() => {
      this.window.rrApi.categoryView(categoryId);
    });
  }

  onAddToCart({ entry, activeBaseStoreCode }: { entry: OrderEntry; activeBaseStoreCode: string }): void {
    this.sendEvent(() => {
      this.window.rrApi.addToBasket(+entry.product.code, { stockId: activeBaseStoreCode });
    });
  }

  recomMouseDown(code: string, name: string, listName?: string): void {
    // обрезаем значение после запятой по требованию ритейлрокета
    let formattedListName: string;

    if (listName) {
      formattedListName = listName.split(',')[0];
    }

    this.sendEvent(() => {
      this.window.rrApi.recomMouseDown(+code, { methodName: name, suggester: formattedListName });
    });
  }

  recomAddToCart(code: string, name: string, listName?: string): void {
    // обрезаем значение после запятой по требованию ритейлрокета
    let formattedListName: string;

    if (listName) {
      formattedListName = listName.split(',')[0];
    }

    this.sendEvent(() => {
      this.window.rrApi.recomAddToCart(+code, { methodName: name, suggester: formattedListName });
    });
  }

  setCustomer(params: {
    customerId: string;
    phone: string;
    email: string;
    defaultStockId: string;
    customData: Record<string, string>;
  }): void {
    this.sendEvent(() => this.window.rrApi.setCustomer(params));
  }

  onConfirmOrder({
    order,
    orderEmail,
    activeBaseStoreCode,
    user,
  }: {
    order: CustomOrder;
    orderEmail: string;
    activeBaseStoreCode: string;
    user: User;
  }): void {
    // Не отправлять события по тестовым заказам
    if (/-PERF-/.test(order.code)) {
      return;
    }

    this.sendEvent(() => {
      const email = orderEmail || order.orgCustomer?.email || user.email || '';
      if (email) {
        this.setCustomer({
          customerId: user.customerId,
          email,
          phone: user.phone,
          customData: { name: user.firstName, surname: user.lastName, birth_date: user.dateOfBirth },
          defaultStockId: activeBaseStoreCode,
        });
        this.window.rrApi.welcomeSequence(email);
      }
      this.window.rrApi.order({
        transaction: order.code,
        items: order.entries.map((entry) => ({
          id: +entry.product.code,
          qnt: entry.quantity,
          price: entry.basePrice.value,
        })),
        stockId: activeBaseStoreCode,
      });
    });
  }

  onLaunchSearch({ searchText }: { searchText: string }): void {
    this.sendEvent(() => {
      this.window.rrApi.search(searchText);
    });
  }

  getSessionId(): Observable<string> {
    return this.sessionId$.asObservable();
  }

  private initDebug(): void {
    const window = this.window;

    const debug =
      (name: string) =>
      (...args: any[]): void => {
        // eslint-disable-next-line no-console
        console.log(`RR - ${name}: `, args);
      };

    window.rrApi = {
      addToBasket: debug('addToBasket'),
      order: debug('order'),
      categoryView: debug('categoryView'),
      view: debug('view'),
      recomMouseDown: debug('recomMouseDown'),
      recomAddToCart: debug('recomAddToCart'),
      setEmail: debug('setEmail'),
      search: debug('search'),
      welcomeSequence: debug('welcomeSequence'),
      setCustomer: debug('setCustomer'),
    };

    window.rrApiOnReady = {
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      push: (f: () => void) => f(),
    };
  }

  private extractSessionFromCookie(): void {
    if (this.cookieService.hasKey('rcuid')) {
      this.sessionId$.next(this.cookieService.get('rcuid'));
    } else {
      this.sessionId$.next(null);
    }
  }

  private sendEvent(func: () => void): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    const window = this.window;
    (window.rrApiOnReady = window.rrApiOnReady || []).push(function (): void {
      try {
        func();
      } catch (e) {}
    });
  }
}
